/**
 * Primitivas da interface.
 *
 * Vocabulário do Vercel/Geist: borda de 1px no lugar de sombra, raio pequeno,
 * contraste alto, ação primária em preto. O laranja da marca aparece pouco —
 * numa interface cinza, o acento só tem força se for raro.
 *
 * Regra que atravessa tudo: alvo de toque nunca abaixo de 40px, e 44px nos
 * controles principais. O alvo continua sendo o dedo.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import { cx } from '../../lib/cx.ts';

// ─────────────────────────── Superfície ───────────────────────────

export function Card({
  children,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  return <Tag className={cx('card', className)}>{children}</Tag>;
}

/** Cabeçalho de seção dentro de um card. */
export function TituloSecao({ children, acao }: { children: ReactNode; acao?: ReactNode }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h3 className="text-sm font-medium text-[var(--fg)]">{children}</h3>
      {acao}
    </div>
  );
}

// ─────────────────────────── Botão ───────────────────────────

type BotaoProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: 'primario' | 'secundario' | 'fantasma' | 'perigo';
  tamanho?: 'sm' | 'md' | 'lg';
  icone?: ReactNode;
  carregando?: boolean;
  larguraTotal?: boolean;
};

export function Botao({
  children,
  variante = 'secundario',
  tamanho = 'md',
  icone,
  carregando,
  larguraTotal,
  className,
  disabled,
  ...rest
}: BotaoProps) {
  const dimensao = {
    sm: 'h-8 px-3 text-[13px] gap-1.5',
    md: 'h-10 px-4 text-sm gap-2',
    lg: 'h-11 px-5 text-sm gap-2',
  }[tamanho];

  const aparencia = {
    primario:
      'bg-[var(--primary)] text-[var(--primary-fg)] hover:bg-[var(--primary-hover)] border border-transparent',
    secundario:
      'bg-[var(--surface)] text-[var(--fg)] border border-[var(--border-strong)] hover:bg-[var(--surface-hover)]',
    fantasma: 'bg-transparent text-[var(--muted)] border border-transparent hover:text-[var(--fg)]',
    perigo:
      'bg-transparent text-[var(--danger)] border border-[var(--border-strong)] hover:bg-[var(--danger-subtle)]',
  }[variante];

  return (
    <button
      {...rest}
      disabled={disabled || carregando}
      className={cx(
        'anel-foco inline-flex items-center justify-center rounded-[var(--r-md)] font-medium',
        'transition-colors duration-100 select-none',
        'disabled:opacity-40 disabled:pointer-events-none',
        dimensao,
        aparencia,
        larguraTotal && 'w-full',
        className,
      )}
    >
      {carregando ? <Spinner /> : icone}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 shrink-0 animate-spin rounded-full border-[1.5px] border-current border-t-transparent"
    />
  );
}

// ─────────────────────────── Campo ───────────────────────────

type CampoProps = InputHTMLAttributes<HTMLInputElement> & {
  rotulo: string;
  erro?: string;
  dica?: string;
};

export function Campo({ rotulo, erro, dica, className, id, ...rest }: CampoProps) {
  const campoId = id ?? `campo-${rotulo.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="w-full">
      <label htmlFor={campoId} className="mb-1.5 block text-sm font-medium text-[var(--fg)]">
        {rotulo}
      </label>
      <input
        {...rest}
        id={campoId}
        aria-invalid={Boolean(erro)}
        aria-describedby={erro ? `${campoId}-erro` : dica ? `${campoId}-dica` : undefined}
        className={cx(
          'anel-foco h-10 w-full rounded-[var(--r-md)] px-3',
          'bg-[var(--surface)] text-[var(--fg)] placeholder:text-[var(--faint)]',
          'border transition-colors',
          erro ? 'border-[var(--danger)]' : 'border-[var(--border-strong)]',
          // 16px no celular evita o zoom automático do iOS ao focar o campo.
          'text-base sm:text-sm',
          className,
        )}
      />
      {erro ? (
        <p id={`${campoId}-erro`} className="mt-1.5 text-[13px] text-[var(--danger)]">
          {erro}
        </p>
      ) : dica ? (
        <p id={`${campoId}-dica`} className="mt-1.5 text-[13px] text-[var(--muted)]">
          {dica}
        </p>
      ) : null}
    </div>
  );
}

// ─────────────────────────── Indicadores ───────────────────────────

export function Etiqueta({
  children,
  tom = 'neutro',
}: {
  children: ReactNode;
  tom?: 'neutro' | 'sucesso' | 'alerta' | 'perigo' | 'marca';
}) {
  const estilo = {
    neutro: 'text-[var(--muted)] bg-[var(--gray-100)] border-[var(--border)]',
    sucesso: 'text-[var(--success)] bg-[var(--success-subtle)] border-transparent',
    alerta: 'text-[var(--warn)] bg-[var(--warn-subtle)] border-transparent',
    perigo: 'text-[var(--danger)] bg-[var(--danger-subtle)] border-transparent',
    marca: 'text-[var(--brand)] bg-[var(--brand-subtle)] border-[var(--brand-border)]',
  }[tom];

  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-[var(--r-full)] border px-2 py-0.5 text-xs font-medium',
        estilo,
      )}
    >
      {children}
    </span>
  );
}

export function Esqueleto({ className }: { className?: string }) {
  return <div aria-hidden className={cx('carregando rounded-[var(--r-md)]', className)} />;
}

export function EstadoVazio({
  icone,
  titulo,
  descricao,
  acao,
}: {
  icone: ReactNode;
  titulo: string;
  descricao: string;
  acao?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <div className="mb-3 grid size-9 place-items-center rounded-[var(--r-md)] border border-[var(--border)] text-[var(--faint)]">
        {icone}
      </div>
      <h3 className="text-sm font-medium text-[var(--fg)]">{titulo}</h3>
      <p className="mt-1 max-w-sm text-[13px] leading-relaxed text-[var(--muted)]">{descricao}</p>
      {acao && <div className="mt-4">{acao}</div>}
    </div>
  );
}

/**
 * Aviso em linha. Discreto de propósito: informa sem sequestrar a tela, que é
 * o erro clássico do banner grande no topo.
 */
export function Aviso({
  tom = 'neutro',
  icone,
  children,
  acao,
}: {
  tom?: 'neutro' | 'alerta';
  icone?: ReactNode;
  children: ReactNode;
  acao?: ReactNode;
}) {
  const estilo =
    tom === 'alerta'
      ? 'border-[var(--brand-border)] bg-[var(--brand-subtle)] text-[var(--fg)]'
      : 'border-[var(--border)] bg-[var(--bg-subtle)] text-[var(--muted)]';

  return (
    <div
      className={cx(
        'flex items-center gap-2.5 rounded-[var(--r-md)] border px-3 py-2 text-[13px]',
        estilo,
      )}
    >
      {icone && <span className="shrink-0 text-[var(--brand)]">{icone}</span>}
      <span className="min-w-0 flex-1">{children}</span>
      {acao}
    </div>
  );
}

/**
 * Alternador de opções. A opção escolhida leva o contorno laranja de
 * `selecionado` — o fundo branco sobre cinza de antes sumia no celular.
 * O rótulo aceita conteúdo (contagem, bolinha de cor) além de texto.
 */
export function Segmentado<T extends string>({
  opcoes,
  valor,
  onChange,
  rotulo,
}: {
  opcoes: { valor: T; rotulo: ReactNode }[];
  valor: T;
  onChange: (v: T) => void;
  rotulo: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={rotulo}
      className="inline-flex items-center gap-1 rounded-[var(--r-md)] border border-[var(--border)] bg-[var(--bg-subtle)] p-1"
    >
      {opcoes.map((o) => {
        const ativo = o.valor === valor;
        return (
          <button
            key={o.valor}
            role="tab"
            aria-selected={ativo}
            onClick={() => onChange(o.valor)}
            className={cx(
              'anel-foco inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[var(--r-sm)] border border-transparent px-3 text-[13px] font-medium whitespace-nowrap transition-colors',
              ativo ? 'selecionado' : 'text-[var(--muted)] hover:text-[var(--fg)]',
            )}
          >
            {o.rotulo}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Caixa de seleção desenhada, no lugar da nativa.
 *
 * A do navegador muda de tamanho e de alinhamento de um aparelho para outro,
 * some no tema escuro e tem 16px — pequena para o dedo. Esta tem 20px visíveis
 * dentro de um alvo de 40px, e marcada fica no laranja da seleção.
 *
 * Interrompe a propagação do clique: ela costuma morar dentro de uma linha que
 * também é clicável (abrir detalhes), e marcar não pode abrir junto.
 */
export function CaixaSelecao({
  marcada,
  indeterminada,
  onChange,
  rotulo,
  className,
}: {
  marcada: boolean;
  indeterminada?: boolean;
  onChange: () => void;
  rotulo: string;
  className?: string;
}) {
  const cheia = marcada || indeterminada;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminada ? 'mixed' : marcada}
      aria-label={rotulo}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className={cx(
        'anel-foco group grid size-10 shrink-0 place-items-center rounded-[var(--r-md)]',
        className,
      )}
    >
      <span
        aria-hidden
        className={cx(
          'grid size-5 place-items-center rounded-[5px] border-2 transition-colors',
          cheia
            ? 'border-[var(--brand)] bg-[var(--brand)] text-white'
            : 'border-[var(--border-strong)] bg-[var(--surface)] group-hover:border-[var(--muted)]',
        )}
      >
        {indeterminada ? (
          <Minus size={13} strokeWidth={3} />
        ) : marcada ? (
          <Check size={13} strokeWidth={3} />
        ) : null}
      </span>
    </button>
  );
}
